import { Badge, Button, Callout, Card, EmptyState, Input, PageHeader, Skeleton, type BadgeTone } from '@provenance/design';
import { UserRole } from '@provenance/shared';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  FileText,
  Inbox,
  Link2,
  Mail,
  MailCheck,
  Search,
  Send,
  Upload,
  UserCircle2,
  Webhook,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { auDateTime } from '../lib/format';
import { useSession } from '../lib/session';
import type { SupportChamber } from '../lib/support';
import { useDebounced } from '../lib/useDebounced';

type TrailRef = { type: 'document' | 'address' | 'organisation'; key: string };

interface SearchResult {
  kind: 'exporter' | 'signatory' | 'person' | 'request' | 'email-in' | 'email-out' | 'upload';
  id: string;
  chamberId: string;
  chamberName: string;
  title: string;
  subtitle: string;
  at: string | null;
  trail: TrailRef;
}

interface TrailEntry {
  at: string;
  source: 'link' | 'upload' | 'email-in' | 'request' | 'email-out' | 'integrator';
  title: string;
  detail: string | null;
  tone: BadgeTone;
  documentId: string | null;
}

interface Trail {
  subject: { type: TrailRef['type']; title: string; subtitle: string; documentId: string | null };
  entries: TrailEntry[];
}

const KIND: Record<SearchResult['kind'], { label: string; icon: ReactNode }> = {
  exporter: { label: 'Exporter', icon: <Building2 className="size-4" /> },
  signatory: { label: 'Signatory', icon: <UserCircle2 className="size-4" /> },
  person: { label: 'Person', icon: <UserCircle2 className="size-4" /> },
  request: { label: 'Request', icon: <FileText className="size-4" /> },
  'email-in': { label: 'Email received', icon: <Inbox className="size-4" /> },
  'email-out': { label: 'Email sent', icon: <Send className="size-4" /> },
  upload: { label: 'Upload', icon: <Upload className="size-4" /> },
};

const SOURCE: Record<TrailEntry['source'], { label: string; icon: ReactNode }> = {
  link: { label: 'Upload link', icon: <Link2 className="size-3.5" /> },
  upload: { label: 'Upload', icon: <Upload className="size-3.5" /> },
  'email-in': { label: 'Email in', icon: <Mail className="size-3.5" /> },
  request: { label: 'Request', icon: <FileText className="size-3.5" /> },
  'email-out': { label: 'Email out', icon: <MailCheck className="size-3.5" /> },
  integrator: { label: 'Integrator', icon: <Webhook className="size-3.5" /> },
};

const trailHref = (t: TrailRef) => `/support-desk/trail?type=${t.type}&key=${encodeURIComponent(t.key)}`;

/**
 * The support desk (T-145): "I sent it and it vanished" answered from one search box.
 *
 * Provenir Support searches every chamber and steps into the result's chamber to see its
 * trail; a chamber administrator searches their own.
 */
export function SupportDesk() {
  const { user, chooseChamber } = useSession();
  const isSupport = user?.role === UserRole.PLATFORM_SUPPORT;
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const term = useDebounced(q.trim(), 300);

  const results = useQuery({
    queryKey: ['support-search', isSupport, term],
    queryFn: () =>
      api.get<SearchResult[]>(isSupport ? '/api/console/support/search' : '/api/console/support-desk/search', { q: term }),
    enabled: term.length >= 2,
  });
  const chambers = useQuery({
    queryKey: ['support-chambers'],
    queryFn: () => api.get<SupportChamber[]>('/api/console/support/chambers'),
    enabled: isSupport,
  });

  /** Support looks closer from inside the result's chamber, so row-level security applies. */
  const open = (r: SearchResult) => {
    if (isSupport && r.chamberId !== user?.chamberId) {
      const chamber = chambers.data?.find((c) => c.id === r.chamberId);
      if (chamber) chooseChamber(chamber, trailHref(r.trail));
      return true;
    }
    return false;
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Find anything"
        lede={
          isSupport
            ? 'Search every chamber by email address, person, company, ABN, certificate number, reference or email subject. Opening a result takes you into its chamber.'
            : 'Search your chamber by email address, person, company, ABN, certificate number, reference or email subject, then follow what happened.'
        }
      />
      <div className="relative max-w-xl">
        <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--pv-faint)]" />
        <Input
          aria-label="Search"
          autoFocus
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setParams(e.target.value ? { q: e.target.value } : {}, { replace: true });
          }}
          placeholder="jane@exporter.com.au, 51 824 753 556, BCQ-2026-00123…"
          className="pl-9"
        />
      </div>

      {term.length < 2 ? null : results.isLoading ? (
        <Skeleton className="h-48" />
      ) : results.error ? (
        <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Search failed">
          {results.error instanceof ApiError ? results.error.message : 'Try again.'}
        </Callout>
      ) : (results.data ?? []).length === 0 ? (
        <Card>
          <EmptyState
            icon={<Search className="size-5" />}
            title="Nothing found"
            description="Try part of the address, the company name, or the reference without its prefix."
          />
        </Card>
      ) : (
        <Card padded={false}>
          <ul className="m-0 list-none p-0">
            {results.data!.map((r) => (
              <li key={`${r.kind}-${r.id}`} className="border-b border-[var(--pv-line)] last:border-b-0">
                <Link
                  to={trailHref(r.trail)}
                  onClick={(e) => {
                    if (open(r)) e.preventDefault();
                  }}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--pv-paper)] focus-visible:outline-2 focus-visible:outline-[var(--pv-focus)]"
                >
                  <span aria-hidden className="text-[var(--pv-faint)]">{KIND[r.kind].icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate [font-size:13.5px] font-medium">{r.title}</span>
                    <span className="block truncate [font-size:12px] text-[var(--pv-muted)]">{r.subtitle}</span>
                  </span>
                  <Badge tone="neutral" size="sm">{KIND[r.kind].label}</Badge>
                  {isSupport && <Badge tone="info" size="sm">{r.chamberName}</Badge>}
                  {r.at && <span className="hidden pv-num [font-size:12px] text-[var(--pv-faint)] sm:inline">{auDateTime(r.at)}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/** One timeline: a request, an address or an exporter, newest first. */
export function SupportTrail() {
  const [params] = useSearchParams();
  const type = params.get('type') as TrailRef['type'] | null;
  const key = params.get('key') ?? '';

  const trail = useQuery({
    queryKey: ['support-trail', type, key],
    queryFn: () =>
      type === 'address'
        ? api.get<Trail>('/api/console/support-desk/trail/address', { address: key })
        : api.get<Trail>(`/api/console/support-desk/trail/${type}/${encodeURIComponent(key)}`),
    enabled: Boolean(type && key),
  });

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/support-desk"
        className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
      >
        <ArrowLeft aria-hidden className="size-3.5" /> Back to search
      </Link>

      {trail.isLoading ? (
        <Skeleton className="h-96" />
      ) : !trail.data ? (
        <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Not available">
          {trail.error instanceof ApiError ? trail.error.message : 'That record is not in this chamber.'}
        </Callout>
      ) : (
        <>
          <PageHeader
            title={trail.data.subject.title}
            lede={trail.data.subject.subtitle}
            actions={
              trail.data.subject.documentId ? (
                <Button variant="secondary" asChild leadingIcon={<FileText className="size-3.5" />}>
                  <Link to={`/documents/${trail.data.subject.documentId}`}>Open the request</Link>
                </Button>
              ) : undefined
            }
          />
          {trail.data.entries.length === 0 ? (
            <Card>
              <EmptyState
                icon={<Inbox className="size-5" />}
                title="Nothing on record"
                description="No upload, link request or email involving this has reached the chamber. If the person is sure they sent it, it did not arrive — check the address they used."
              />
            </Card>
          ) : (
            <Card padded={false}>
              <ol className="m-0 list-none p-0">
                {trail.data.entries.map((e, i) => (
                  <li key={`${e.at}-${i}`} className="flex gap-3 border-b border-[var(--pv-line)] px-5 py-3 last:border-b-0">
                    <span className="w-36 shrink-0 pv-num [font-size:12px] text-[var(--pv-muted)]">{auDateTime(e.at)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge tone={e.tone} size="sm">
                          {SOURCE[e.source].icon}
                          {SOURCE[e.source].label}
                        </Badge>
                        <span className="[font-size:13.5px]">{e.title}</span>
                      </span>
                      {e.detail && <span className="mt-0.5 block [font-size:12px] text-[var(--pv-muted)]">{e.detail}</span>}
                    </span>
                    {e.documentId && e.documentId !== trail.data.subject.documentId && (
                      <Link to={`/documents/${e.documentId}`} className="shrink-0 [font-size:12px] underline">
                        Request
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
